import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAllPositions, selectPosition } from '@store/masterData/positionSlice';
import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { FileParameter, FishingTripPeriodReportDataResponse, ReportDataResponse, ReportsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { getAllLedgerCategory, selectLedgerCategory } from '../masterData/ledgerCategorySlice';

export interface FishingTripPeriodReportData extends FishingTripPeriodReportDataResponse { }

export interface ReportData extends ReportDataResponse {
    categoryDescription: string;
    categorySortOrder: number;
    workerPositionSortOrder: number;
}

export interface State {
    isEndOfTripReportDataListLoading: boolean;
    isPeriodReportDataListLoading: boolean;
    isLabourAdvanceReportDataListLoading: boolean;
    isWashupReportDataListLoading: boolean;
    isExtrasReportDataListLoading: boolean;
    error: boolean;

    fishingTripPeriodReportDataList: FishingTripPeriodReportData[];
    labourAdvanceReportDataList: ReportData[];
    endOfTripReportDataList: ReportData[];
    washupReportDataList: ReportData[];
    extrasReportDataList: ReportData[];

    isAllWorkerSummaryReportsAreReady: boolean;
}

const initialState: State = {
    isEndOfTripReportDataListLoading: true,
    isPeriodReportDataListLoading: true,
    isLabourAdvanceReportDataListLoading: true,
    isWashupReportDataListLoading: true,
    isExtrasReportDataListLoading: true,
    error: false,

    fishingTripPeriodReportDataList: [],
    endOfTripReportDataList: [],
    labourAdvanceReportDataList: [],
    washupReportDataList: [],
    extrasReportDataList: [],

    isAllWorkerSummaryReportsAreReady: false,
};

const reportSlice = createSlice({
    name: 'report',
    initialState,
    reducers: {
        startEndOfTripReportDataListLoading: (state) => {
            state.isEndOfTripReportDataListLoading = true;
            state.error = false;
        },
        startPeriodReportDataListLoading: (state) => {
            state.isPeriodReportDataListLoading = true;
            state.error = false;
        },
        startLabourAdvanceReportDataListLoading: (state) => {
            state.isLabourAdvanceReportDataListLoading = true;
            state.error = false;
        },
        startWashupReportDataListLoading: (state) => {
            state.isWashupReportDataListLoading = true;
            state.error = false;
        },
        startExtrasReportDataListLoading: (state) => {
            state.isExtrasReportDataListLoading = true;
            state.error = false;
        },
        hasError: (state, action) => {
            state.isEndOfTripReportDataListLoading = false;
            state.isPeriodReportDataListLoading = false;
            state.isLabourAdvanceReportDataListLoading = false;
            state.isWashupReportDataListLoading = false;
            state.isExtrasReportDataListLoading = false;
            state.error = action.payload;
        },
        getPeriodReportDataListSuccess: (state, action: PayloadAction<FishingTripPeriodReportData[]>) => {
            state.fishingTripPeriodReportDataList = action.payload;
            state.isPeriodReportDataListLoading = false;
        },
        getEndOfTripReportDataListSuccess: (state, action: PayloadAction<ReportData[]>) => {
            state.endOfTripReportDataList = action.payload;
            state.isEndOfTripReportDataListLoading = false;
        },
        getLabourAdvanceReportDataListSuccess: (state, action: PayloadAction<ReportData[]>) => {
            state.labourAdvanceReportDataList = action.payload;
            state.isLabourAdvanceReportDataListLoading = false;
        },
        getWashupReportDataListSuccess: (state, action: PayloadAction<ReportData[]>) => {
            state.washupReportDataList = action.payload;
            state.isWashupReportDataListLoading = false;
        },
        getExtrasReportDataListSuccess: (state, action: PayloadAction<ReportData[]>) => {
            state.extrasReportDataList = action.payload;
            state.isExtrasReportDataListLoading = false;
        },
        checkAllWorkerSummaryReportsAreReadySuccess: (state, action: PayloadAction<boolean>) => {
            state.isAllWorkerSummaryReportsAreReady = action.payload;
        },
    },
});

// Reducer
export default reportSlice.reducer

// Actions
export const { hasError } = reportSlice.actions;

// for useSelector
export const selectReport = (state: RootState) => state.report as State;

// ----------------------------------------------------------------------
async function addFieldsInfo(dispatch: any, getState: any, response: ReportDataResponse[]) {
    await dispatch(getAllLedgerCategory());
    const { ledgerCategoryList } = selectLedgerCategory(getState());
    const ledgerCategoryMap = new Map(ledgerCategoryList.map(lc => [lc.ledgerCategoryCode, lc]));

    await dispatch(getAllPositions());
    const { positionList } = selectPosition(getState());
    const positionMap = new Map(positionList.map(p => [p.positionCode, p.sortOrder]));

    return response.map(r => {
        const ledgerCategory = ledgerCategoryMap.get(r.category);
        const workerPositionSortOrder = positionMap.get(r.workerPosition) ?? 0;
        return {
            ...r,
            categoryDescription: ledgerCategory?.ledgerCategoryDescription ?? r.category,
            categorySortOrder: ledgerCategory?.ledgerCategorySortOrder ?? 0,
            workerPositionSortOrder
        };
    });
}

export function getPeriodReportData(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(reportSlice.actions.startPeriodReportDataListLoading());
        try {
            const client = new ReportsClient(undefined, axios);
            const response = await client.getFishingTripPeriodReportData(fishingTripNumber);
            dispatch(reportSlice.actions.getPeriodReportDataListSuccess(response));
            return response;
        } catch (error) {
            dispatch(reportSlice.actions.hasError(true));
            handleError(error);
            return [];
        }
    };
}

export function getLabourAdvanceReportData(exportId: string) {
    return async (dispatch, getState) => {
        dispatch(reportSlice.actions.startLabourAdvanceReportDataListLoading());
        try {
            const client = new ReportsClient(undefined, axios);
            const response = await client.getReportData(exportId);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(reportSlice.actions.getLabourAdvanceReportDataListSuccess(payload));
            return response;
        } catch (error) {
            dispatch(reportSlice.actions.hasError(true));
            handleError(error);
            return [];
        }
    };
}

export function getWashupReportData(exportId: string) {
    return async (dispatch, getState) => {
        dispatch(reportSlice.actions.startWashupReportDataListLoading());
        try {
            const client = new ReportsClient(undefined, axios);
            const response = await client.getReportData(exportId);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(reportSlice.actions.getWashupReportDataListSuccess(payload));
            return response;
        } catch (error) {
            dispatch(reportSlice.actions.hasError(true));
            handleError(error);
            return [];
        }
    };
}

export function getExtrasReportData(exportId: string) {
    return async (dispatch, getState) => {
        dispatch(reportSlice.actions.startExtrasReportDataListLoading());
        try {
            const client = new ReportsClient(undefined, axios);
            const response = await client.getReportData(exportId);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(reportSlice.actions.getExtrasReportDataListSuccess(payload));
            return response;
        } catch (error) {
            dispatch(reportSlice.actions.hasError(true));
            handleError(error);
            return [];
        }
    };
}

export function getEndOfTripReportData(exportId: string) {
    return async (dispatch, getState) => {
        dispatch(reportSlice.actions.startEndOfTripReportDataListLoading());
        try {
            const client = new ReportsClient(undefined, axios);
            const response = await client.getReportData(exportId);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(reportSlice.actions.getEndOfTripReportDataListSuccess(payload));
            return response;
        } catch (error) {
            dispatch(reportSlice.actions.hasError(true));
            handleError(error);
            return [];
        }
    };
}

export async function sendReportEmail(reportData: Blob, subject: string,
    reportName: string, userName: string, email: string, toastId: ReactText) {
    try {
        const client = new ReportsClient(undefined, axios);

        const attachment = {
            data: reportData,
            fileName: reportName,
        } as FileParameter;

        var result = await client.sendReportEmail(subject, userName, email, attachment);
        if (result) {
            toast.update(toastId, { render: stringResources.EMAIL.SEND_SUCCESS, type: 'success', isLoading: false, autoClose: 3000 });
        } else {
            toast.update(toastId, { render: stringResources.EMAIL.SEND_FAILED, type: 'error', isLoading: false, autoClose: 3000 });
        }
    } catch (error) {
        handleError(error);
        toast.update(toastId, { render: stringResources.EMAIL.SEND_FAILED, type: 'error', isLoading: false, autoClose: 3000 });
    }
}

export async function sendAllWorkerSummaryReportEmails(fishingTripNumber: string) {
    try {
        const client = new ReportsClient(undefined, axios);
        await client.sendAllWorkerSummaryReportEmails(fishingTripNumber);
        toast.success('Emailing worker summary reports to all crew in background!');
    } catch (error) {
        handleError(error);
    }
}

export async function sendEmailWithReportUrl(subject: string, userName: string, userEmail: string, reportUrl: string, toastId: ReactText) {
    try {
        const client = new ReportsClient(undefined, axios);
        var result = await client.sendEmailWithReportUrl(subject, userName, userEmail, reportUrl);
        if (result) {
            toast.update(toastId, { render: stringResources.EMAIL.SEND_SUCCESS, type: 'success', isLoading: false, autoClose: 3000 });
        } else {
            toast.update(toastId, { render: stringResources.EMAIL.SEND_FAILED, type: 'error', isLoading: false, autoClose: 3000 });
        }

    } catch (error) {
        handleError(error);
        toast.update(toastId, { render: stringResources.EMAIL.SEND_FAILED, type: 'error', isLoading: false, autoClose: 3000 });
    }
}

export function checkAllWorkerSummaryReportsAreReady(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        try {
            const client = new ReportsClient(undefined, axios);
            var response = await client.checkAllWorkerSummaryReportAreReady(fishingTripNumber);
            dispatch(reportSlice.actions.checkAllWorkerSummaryReportsAreReadySuccess(response));
            return response;
        } catch (error) {
            handleError(error);
        }
    }
}

export const updateEotReportGeneratingFlag = async (fishingTripNumber: string, isGenerating: boolean) => {
    try {
        const client = new ReportsClient(undefined, axios);
        await client.updateEotReportGeneratingFlag(fishingTripNumber, isGenerating);
    } catch (error: any) {
        handleError(error);
    }
}

export function viewReport(url: string) {
    return async (dispatch, getState) => {
        try {
            const client = new ReportsClient(undefined, axios);
            var response = await client.viewReport(url);
        return response
        } catch (error) {
            console.log(error);
            dispatch(reportSlice.actions.hasError(true));
            handleError(error);
            return 'Error';
        }
    }
}